import { useMutation } from '@tanstack/react-query';
import { type Hex } from 'viem';
import { useWalletClient } from 'wagmi';
import { MessageTransmitterV2Abi } from '../abis/MessageTransmitterV2Abi';
import { getCctpConfig } from '../config/cctp';

export interface CctpReceiveParams {
  /** Raw message bytes from the MessageSent event (parsed from depositForBurn receipt) */
  message: Hex;
  /** Attestation signature from Circle's Attestation API */
  attestation: Hex;
}

export interface CctpReceiveResult {
  /** Transaction hash of the receiveMessage call */
  txHash: `0x${string}`;
}

/**
 * Returns a mutation function to execute `receiveMessage` on the CCTP
 * MessageTransmitterV2 contract on the destination chain.
 *
 * This finalises a cross-chain USDC transfer by submitting the message bytes
 * and Circle attestation to mint USDC to the recipient on the destination chain.
 *
 * The connected wallet must be on `destinationChainId` when the mutation is called.
 *
 * @param destinationChainId - EVM chain ID where USDC will be minted (destination)
 */
export function useCctpReceive(destinationChainId: number | undefined) {
  const { data: walletClient } = useWalletClient({
    chainId: destinationChainId,
  });

  return useMutation({
    mutationFn: async (
      params: CctpReceiveParams,
    ): Promise<CctpReceiveResult> => {
      if (!destinationChainId) {
        throw new Error('destinationChainId is required');
      }
      if (!walletClient) {
        throw new Error('Wallet not connected');
      }

      const destConfig = getCctpConfig(destinationChainId);

      const txHash = await walletClient.writeContract({
        address: destConfig.messageTransmitter,
        abi: MessageTransmitterV2Abi,
        functionName: 'receiveMessage',
        args: [params.message, params.attestation],
        chain: walletClient.chain,
        account: walletClient.account,
      });

      return { txHash };
    },
  });
}
